/**
 * API functions for Attendance Reports editing
 */
import { BACKEND_API_BASE_URL } from '../config';

const API_BASE_URL = `${BACKEND_API_BASE_URL}/api`;

/**
 * Update an attendance record (change status or notes)
 * @param {number} recordId - The attendance record ID
 * @param {object} data - The data to update (status, notes)
 * @param {string} token - Auth token
 * @returns {Promise<object>} Updated attendance record
 */
export async function updateAttendanceRecord(recordId, data, token) {
  const response = await fetch(`${API_BASE_URL}/attendance/records/${recordId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update attendance record');
  }

  return response.json();
}

/**
 * Add a student to an existing attendance session
 * @param {number} sessionId - The attendance session ID
 * @param {number} studentId - The student ID to add
 * @param {string} status - The attendance status (default: 'PRESENT')
 * @param {string} token - Auth token
 * @returns {Promise<object>} Created attendance record
 */
export async function addStudentToSession(sessionId, studentId, status = 'PRESENT', token) {
  const response = await fetch(`${API_BASE_URL}/attendance/${sessionId}/records`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      studentId,
      status,
      notes: null
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add student to session');
  }

  return response.json();
}

/**
 * Remove a student from an attendance session (soft delete the record)
 * @param {number} recordId - The attendance record ID
 * @param {string} token - Auth token
 * @returns {Promise<void>}
 */
export async function removeStudentFromSession(recordId, token) {
  const response = await fetch(`${API_BASE_URL}/attendance/records/${recordId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok && response.status !== 204) {
    const error = await response.json().catch(() => ({ error: 'Failed to remove student' }));
    throw new Error(error.error || 'Failed to remove student from session');
  }
}

/**
 * Get all students in a classroom (for adding to reports)
 * @param {number} classroomId - The classroom ID
 * @param {string} token - Auth token
 * @returns {Promise<array>} List of students
 */
export async function getClassroomStudents(classroomId, token) {
  const response = await fetch(`${API_BASE_URL}/students/classroom/${classroomId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch classroom students');
  }

  return response.json();
}

/**
 * Get list of classroom IDs that the current user can modify
 * @param {string} token - Auth token
 * @returns {Promise<object>} Object with accessType ('all' or 'restricted') and classroomIds array
 */
export async function getUserAccessibleClassrooms(token) {
  const response = await fetch(`${API_BASE_URL}/attendance/user/accessible-classrooms`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch accessible classrooms');
  }

  return await response.json();
}
